import cx from 'classnames';
import type { ChangeEvent, ElementRef, FocusEvent } from 'react';
import { forwardRef, useCallback, useId, useState } from 'react';
import { useIsKeyboardNavigationState } from '../../hooks/useKeyboardNavigation.js';
import { useBoxSelectorContext } from './BoxSelectorContext.js';
import type {
  BoxSelectorCheckboxValue,
  BoxSelectorItemProps,
} from './types.js';
import { isBoolean } from './utils.js';

export const BoxSelectorItem = forwardRef<
  ElementRef<'input'>,
  BoxSelectorItem.Props
>(function BoxSelectorItem(
  {
    children,
    value,
    className,
    selectedClassName = '',
    disabledClassName = '',
    mktSelector,
    qaSelector,
    disabled: itemDisabled = false,
  },
  ref,
) {
  const {
    id,
    isSelected,
    disabled,
    selectedValue,
    name,
    type,
    onChange: onChangeHandler,
    classNames,
    onFocus: onFocusHandler,
    onBlur: onBlurHandler,
  } = useBoxSelectorItem({
    value,
    className,
    selectedClassName,
    disabledClassName,
    disabled: itemDisabled,
  });

  const usableValue = typeof value === 'boolean' ? String(value) : value;
  return (
    <label
      htmlFor={id}
      className={classNames}
      data-qa-value={value}
      data-mkt-selector={mktSelector}
      data-qa-selector={qaSelector}
      aria-disabled={disabled}
    >
      {typeof children === 'function'
        ? children({ isSelected, selectedValue })
        : children}
      <input
        ref={ref}
        className="visually-hidden"
        id={id}
        type={type}
        name={name}
        value={usableValue}
        checked={isSelected}
        disabled={disabled}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
      />
    </label>
  );
});

// eslint-disable-next-line @typescript-eslint/no-redeclare
export namespace BoxSelectorItem {
  export interface Props extends BoxSelectorItemProps {}
}

interface UseBoxSelectorItem extends Pick<
  BoxSelectorItem.Props,
  'value' | 'className' | 'selectedClassName' | 'disabledClassName' | 'disabled'
> {}

function useBoxSelectorItem(props: UseBoxSelectorItem) {
  const context = useBoxSelectorContext();
  const isTabbing = useIsKeyboardNavigationState();
  const [hasFocus, setHasFocus] = useState(false);

  const {
    id,
    name,
    value: groupValue,
    type,
    onBlur,
    onFocus,
    onChange,
    disabled,
    isValid,
    isTagBox,
  } = context;
  const {
    value: itemValue,
    className,
    selectedClassName = '',
    disabledClassName = '',
  } = props;

  const genId = useId();

  const isDisabled = disabled || props.disabled;

  const hasFocusRing = !isDisabled && isTabbing && hasFocus;

  const itemId = `${id}_${itemValue ?? genId}`;

  let isSelected: boolean =
    type === 'radio'
      ? itemValue === groupValue
      : Array.isArray(groupValue)
        ? groupValue.includes(itemValue)
        : typeof itemValue === 'undefined' || itemValue === ''
          ? groupValue === true
          : itemValue === groupValue;

  const classNames = cx('box-selector__item', className, {
    'box-selector__item-tag': isTagBox,
    'box-selector__item--is-selected': !isDisabled && isSelected,
    'box-selector__item--is-disabled': isDisabled,
    'box-selector__item--has-ring': hasFocusRing,
    'box-selector__item--is-selected-ring':
      !isDisabled && isSelected && hasFocusRing,
    'box-selector__item--has-error': isBoolean(isValid) ? !isValid : undefined,
    [selectedClassName]: isSelected,
    [disabledClassName]: isDisabled,
  });

  const onBlurHandler = useCallback(_onBlurHandler, [isDisabled, onBlur]);
  const onFocusHandler = useCallback(_onFocusHandler, [isDisabled, onFocus]);
  const onChangeHandler = useCallback(_onChangeHandler, [
    onChange,
    groupValue,
    type,
    itemValue,
  ]);

  return {
    ...context,
    id: itemId,
    isSelected,
    selectedValue: groupValue,
    name,
    value: props.value,
    onBlur: onBlurHandler,
    onFocus: onFocusHandler,
    onChange: onChangeHandler,
    hasFocus: hasFocus,
    hasFocusRing,
    classNames,
    isValid,
    disabled: isDisabled,
  };

  function _onBlurHandler(e: FocusEvent<HTMLInputElement>) {
    if (!isDisabled) {
      onBlur?.(e);
      setHasFocus(false);
    }
  }

  function _onFocusHandler(e: FocusEvent<HTMLInputElement>) {
    if (!isDisabled) {
      onFocus?.(e);
      setHasFocus(true);
    }
  }

  function _onChangeHandler(e: ChangeEvent<HTMLInputElement>) {
    if (type === 'radio') {
      onChange(itemValue, e);
    } else {
      const {
        target: { checked },
      } = e;
      if (Array.isArray(groupValue)) {
        const newValue = checked
          ? [...groupValue.filter((v) => v !== itemValue), itemValue]
          : groupValue.filter((v) => v !== itemValue);
        onChange(newValue, e);
      } else {
        // Single checkbox case
        const newValue = getCheckboxValue(itemValue, checked);
        onChange(newValue, e);
      }
    }
  }
}

function getCheckboxValue(
  value: Exclude<BoxSelectorCheckboxValue, Array<unknown>> | undefined,
  checked: boolean,
) {
  if (typeof value === 'undefined' || value === '') {
    return checked ? true : false;
  }

  return checked ? value : undefined;
}
